import { z } from "zod";
import { hexColor, isoDate, isoDateTime, moneyMinor, optionalText, text, uuid } from "./common";

/* ------------------------------------------------------------------------------------------------
 * Odontogram (dental chart). Tooth codes are always FDI two-digit (11–48 permanent, 51–85 primary);
 * Universal/Palmer are display formats only.
 * ---------------------------------------------------------------------------------------------- */

/** M mesial, O occlusal, D distal, B buccal, L lingual, I incisal, P palatal. */
export const DENTAL_SURFACES = ["M", "O", "D", "B", "L", "I", "P"] as const;
export type DentalSurface = (typeof DENTAL_SURFACES)[number];
export const FDI_TOOTH_PATTERN = /^([1-4][1-8]|[5-8][1-5])$/;
export const NUMBERING_SYSTEMS = ["FDI", "Universal", "Palmer"] as const;
export const DENTITION_MODES = ["permanent", "primary", "mixed"] as const;
export const CHART_ENTRY_STATUSES = ["existing", "planned", "completed"] as const;
export type ChartEntryStatus = (typeof CHART_ENTRY_STATUSES)[number];
export const CONDITION_CATEGORIES = ["pathology", "restoration", "prosthesis", "endodontic", "periodontal", "orthodontic", "missing", "other"] as const;
export const CONDITION_APPLIES_TO = ["tooth", "surface", "root"] as const;

const toothCode = z.string().trim().regex(FDI_TOOTH_PATTERN, "Use an FDI tooth number (11–48 permanent, 51–85 primary)");
const surfaces = z
  .array(z.enum(DENTAL_SURFACES))
  .max(DENTAL_SURFACES.length)
  .default([])
  .refine((s) => new Set(s).size === s.length, "Each surface only once");

/** Codes starting with "_" are reserved for the product (e.g. the removal marker). */
export const conditionTypeInput = z.object({
  code: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9_]{0,63}$/, "Lower-case letters, digits and _"),
  name: text(100),
  category: z.enum(CONDITION_CATEGORIES),
  appliesTo: z.enum(CONDITION_APPLIES_TO).default("tooth"),
  colour: hexColor.nullable().default(null),
  symbol: optionalText(40),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  isActive: z.boolean().default(true),
});
/** System rows may be renamed, recoloured, reordered or deactivated, but keep their code/category/appliesTo. */
export const updateConditionTypeInput = conditionTypeInput;

export interface DentalConditionType {
  id: string;
  code: string;
  name: string;
  category: (typeof CONDITION_CATEGORIES)[number];
  appliesTo: (typeof CONDITION_APPLIES_TO)[number];
  colour: string | null;
  symbol: string | null;
  sortOrder: number;
  isSystem: boolean;
  isActive: boolean;
}

export const chartQuery = z.object({ at: isoDateTime.optional() });
export const chartHistoryQuery = z.object({ tooth: toothCode.optional() });

export const chartSettingsInput = z.object({
  numberingSystem: z.enum(NUMBERING_SYSTEMS),
  dentitionMode: z.enum(DENTITION_MODES),
  version: z.number().int().min(1),
});

export const chartEntryInput = z.object({
  toothCode,
  surfaces,
  conditionTypeId: uuid,
  status: z.enum(CHART_ENTRY_STATUSES).default("existing"),
  notes: optionalText(2000),
  visitId: uuid.nullable().default(null),
  /** The current entry this finding replaces (same tooth). Old entry is kept as history. */
  supersedesEntryId: uuid.nullable().default(null),
});
export type ChartEntryInput = z.infer<typeof chartEntryInput>;

export const removeChartEntryInput = z.object({ reason: text(500) });

export interface ChartEntry {
  id: string;
  toothCode: string;
  dentition: "permanent" | "primary";
  surfaces: DentalSurface[];
  condition: { id: string; code: string; name: string; category: string; colour: string | null };
  status: ChartEntryStatus;
  notes: string | null;
  treatmentPlanItemId: string | null;
  visitId: string | null;
  recordedBy: string | null;
  recordedAt: string;
  supersededById: string | null;
  supersededAt: string | null;
  /** True for a removal marker (the finding it supersedes was removed / entered in error). */
  isRemoval: boolean;
}

export interface DentalChart {
  chart: { id: string | null; numberingSystem: (typeof NUMBERING_SYSTEMS)[number]; dentitionMode: (typeof DENTITION_MODES)[number]; version: number };
  /** Current findings, or the findings as they were at `at`. */
  entries: ChartEntry[];
  legend: DentalConditionType[];
  at: string | null;
}

/* ------------------------------------------------------------------------------------------------
 * Treatment plans
 * ---------------------------------------------------------------------------------------------- */

export const TREATMENT_PLAN_STATUSES = ["draft", "proposed", "accepted", "partially_accepted", "in_progress", "completed", "cancelled"] as const;
export type TreatmentPlanStatus = (typeof TREATMENT_PLAN_STATUSES)[number];
export const TREATMENT_ITEM_STATUSES = ["proposed", "accepted", "scheduled", "in_progress", "completed", "cancelled"] as const;
export type TreatmentItemStatus = (typeof TREATMENT_ITEM_STATUSES)[number];

/** Quantity with at most two decimals (DB numeric(10,2)). */
const quantity = z
  .number()
  .positive()
  .max(99_999_999)
  .refine((q) => Math.abs(Math.round(q * 100) - q * 100) < 1e-6, "At most two decimals");

export const treatmentPlanItemInput = z
  .object({
    serviceId: uuid,
    description: optionalText(300),
    toothCode: toothCode.nullable().default(null),
    surfaces,
    quantity: quantity.default(1),
    discountMinor: moneyMinor.default(0),
    doctorId: uuid.nullable().default(null),
    estimatedDate: isoDate.nullable().default(null),
    notes: optionalText(1000),
  })
  .refine((i) => i.surfaces.length === 0 || i.toothCode, { message: "Surfaces need a tooth", path: ["surfaces"] });

export const treatmentPlanInput = z.object({
  title: optionalText(200),
  /** Defaults to the signed-in doctor. */
  doctorId: uuid.nullable().default(null),
  /** Branch whose prices apply (branch price overrides the clinic-wide price). */
  branchId: uuid.nullable().default(null),
  notes: optionalText(4000),
  items: z.array(treatmentPlanItemInput).max(100).default([]),
});
export type TreatmentPlanInput = z.infer<typeof treatmentPlanInput>;

/** Draft/proposed plans only. Items are replaced and re-priced at today's prices. */
export const updateTreatmentPlanInput = treatmentPlanInput.extend({ version: z.number().int().min(1) });

/** Records 'planned' chart entries for accepted items that have a tooth. */
const plannedChartEntries = z.array(z.object({ itemId: uuid, conditionTypeId: uuid })).max(100).default([]);

export const PLAN_ACTIONS = ["propose", "accept", "accept_items", "cancel", "complete"] as const;
export const planTransitionInput = z
  .object({
    action: z.enum(PLAN_ACTIONS),
    version: z.number().int().min(1),
    itemIds: z.array(uuid).max(100).optional(),
    reason: optionalText(500),
    plannedChartEntries,
  })
  .refine((b) => b.action !== "accept_items" || (b.itemIds && b.itemIds.length > 0), { message: "Choose the items to accept", path: ["itemIds"] })
  .refine((b) => b.action !== "cancel" || b.reason, { message: "A reason is required", path: ["reason"] });

export const PLAN_ITEM_ACTIONS = ["accept", "schedule", "start", "complete", "cancel"] as const;
export type PlanItemAction = (typeof PLAN_ITEM_ACTIONS)[number];
export const planItemTransitionInput = z
  .object({
    action: z.enum(PLAN_ITEM_ACTIONS),
    appointmentId: uuid.optional(),
    reason: optionalText(500),
    /** accept only: record a 'planned' chart entry with this condition (item must have a tooth). */
    conditionTypeId: uuid.optional(),
  })
  .refine((b) => b.action !== "schedule" || b.appointmentId, { message: "Choose the appointment", path: ["appointmentId"] })
  .refine((b) => b.action !== "cancel" || b.reason, { message: "A reason is required", path: ["reason"] });

/** Allowed item transitions (plan must already be proposed or later; drafts are edited directly). */
export const PLAN_ITEM_TRANSITIONS: Record<PlanItemAction, { from: readonly TreatmentItemStatus[]; to: TreatmentItemStatus }> = {
  accept: { from: ["proposed"], to: "accepted" },
  schedule: { from: ["accepted", "scheduled", "in_progress"], to: "scheduled" },
  start: { from: ["accepted", "scheduled"], to: "in_progress" },
  complete: { from: ["accepted", "scheduled", "in_progress"], to: "completed" },
  cancel: { from: ["proposed", "accepted", "scheduled", "in_progress"], to: "cancelled" },
};

export interface TreatmentPlanItem {
  id: string;
  sequence: number;
  service: { id: string; name: string; code: string };
  description: string | null;
  toothCode: string | null;
  surfaces: DentalSurface[];
  quantity: number;
  unitPriceMinor: number;
  discountMinor: number;
  taxRateId: string | null;
  taxRatePercent: number | null;
  taxMinor: number;
  lineTotalMinor: number;
  doctorId: string | null;
  status: TreatmentItemStatus;
  estimatedDate: string | null;
  appointmentId: string | null;
  notes: string | null;
  history: { fromStatus: TreatmentItemStatus | null; toStatus: TreatmentItemStatus; reason: string | null; changedAt: string; changedBy: string | null }[];
}

export interface TreatmentPlan {
  id: string;
  patientId: string;
  doctor: { id: string; fullName: string };
  branchId: string | null;
  title: string | null;
  status: TreatmentPlanStatus;
  currency: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  proposedAt: string | null;
  acceptedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  notes: string | null;
  version: number;
  createdAt: string;
  itemCount: number;
}
export interface TreatmentPlanDetail extends TreatmentPlan {
  items: TreatmentPlanItem[];
}

/* ------------------------------------------------------------------------------------------------
 * Procedures performed (treatment sessions)
 * ---------------------------------------------------------------------------------------------- */

export const PROCEDURE_STATUSES = ["completed", "partial", "aborted", "entered_in_error"] as const;
export const procedureInput = z
  .object({
    serviceId: uuid,
    toothCode: toothCode.nullable().default(null),
    surfaces,
    planItemId: uuid.nullable().default(null),
    status: z.enum(["completed", "partial", "aborted"]).default("completed"),
    stageLabel: optionalText(100),
    notes: optionalText(4000),
    materials: optionalText(1000),
  })
  .refine((i) => i.surfaces.length === 0 || i.toothCode, { message: "Surfaces need a tooth", path: ["surfaces"] });
export type ProcedureInput = z.infer<typeof procedureInput>;

export const enteredInErrorInput = z.object({ reason: text(500) });

export interface Procedure {
  id: string;
  visitId: string;
  patientId: string;
  planItemId: string | null;
  service: { id: string; name: string; code: string };
  toothCode: string | null;
  surfaces: DentalSurface[];
  doctor: { id: string; fullName: string };
  performedAt: string;
  status: (typeof PROCEDURE_STATUSES)[number];
  stageLabel: string | null;
  notes: string | null;
  materials: string | null;
}
